<template>
  <SectionShell id="shapes" num="03" kicker="Shape Vision">
    <template #title>Break the face into<br /><span class="sig">puzzle pieces</span> before<br />you draw a single one.</template>
    <template #lede>
      <p>
        A grid does something no amount of willpower can: it chops a face into
        twenty-five boxes that individually mean nothing. You stop drawing "a nose"
        and start drawing "the dark wedge that touches the left wall of box 13" —
        and your accuracy jumps overnight.
      </p>
    </template>

    <div class="lab">
      <!-- ---------- the viewer ---------- -->
      <figure class="lab__stage card">
        <div class="stage__inner" :class="{ flip: flipped }" :style="stageFilter">
          <HeadForm :light-x="0.72" :light-y="0.24" />
          <svg
            v-if="grid"
            class="stage__grid"
            viewBox="0 0 420 560"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <g :stroke="'rgba(86,225,240,0.42)'" stroke-width="0.7" fill="none" vector-effect="non-scaling-stroke">
              <line v-for="x in cols - 1" :key="'c' + x" :x1="(420 / cols) * x" y1="0" :x2="(420 / cols) * x" y2="560" />
              <line v-for="y in rows - 1" :key="'r' + y" x1="0" :y1="(560 / rows) * y" x2="420" :y2="(560 / rows) * y" />
            </g>
            <g v-if="isolate" fill="rgba(4,6,10,0.86)">
              <rect x="0" y="0" width="420" :height="cellY * focusR" />
              <rect x="0" :y="cellY * (focusR + 1)" width="420" :height="560 - cellY * (focusR + 1)" />
              <rect :y="cellY * focusR" x="0" :width="cellX * focusC" :height="cellY" />
              <rect :y="cellY * focusR" :x="cellX * (focusC + 1)" :width="420 - cellX * (focusC + 1)" :height="cellY" />
            </g>
            <rect
              v-if="isolate"
              :x="cellX * focusC"
              :y="cellY * focusR"
              :width="cellX"
              :height="cellY"
              fill="none"
              stroke="#ffb45c"
              stroke-width="1.4"
              vector-effect="non-scaling-stroke"
            />
          </svg>
        </div>
        <figcaption class="stage__cap mono">
          <span>{{ readout }}</span>
        </figcaption>
      </figure>

      <!-- ---------- the console ---------- -->
      <div class="lab__console">
        <p class="console__hint">
          Four switches. Each one is a real studio trick for shutting down the part of
          your brain that names things.
        </p>

        <div class="ctl">
          <button :aria-pressed="String(grid)" :class="{ 'is-on': grid }" @click="grid = !grid">Grid overlay</button>
          <p>Rule squares over the reference and the same squares, lightly, on your paper. Copy one box at a time.</p>
        </div>

        <div class="ctl" :class="{ dim: !grid }">
          <div class="ctl__row">
            <button :aria-pressed="String(cols === 5)" :class="{ 'is-on': cols === 5 }" @click="setGrid(5, 7)">Coarse 5 × 7</button>
            <button :aria-pressed="String(cols === 10)" :class="{ 'is-on': cols === 10 }" @click="setGrid(10, 14)">Fine 10 × 14</button>
          </div>
          <p>The busier the reference, the smaller the boxes. Fine grids also let you enlarge: draw 25 mm boxes from a 12 mm reference and the drawing comes out at double scale.</p>
        </div>

        <div class="ctl" :class="{ dim: !grid }">
          <button :aria-pressed="String(isolate)" :class="{ 'is-on': isolate }" @click="isolate = !isolate">Isolate one box</button>
          <p>Mask everything else and the box becomes abstract — a couple of grey wedges and an edge. That is all you have to copy. Nothing in there is a "nose".</p>
        </div>

        <div class="ctl">
          <button :aria-pressed="String(flipped)" :class="{ 'is-on': flipped }" @click="flipped = !flipped">Turn it upside down</button>
          <p>The oldest trick in the book, and the strongest. Inverted, your brain gives up on recognition and finally lets you copy what is actually there.</p>
        </div>

        <div class="ctl">
          <button :aria-pressed="String(squint)" :class="{ 'is-on': squint }" @click="squint = !squint">Squint</button>
          <p>Blurring the image collapses detail and leaves only the big value masses. If the shapes still read when squinted, the drawing will read across a room.</p>
        </div>

        <div class="ctl">
          <button :aria-pressed="String(poster)" :class="{ 'is-on': poster }" @click="poster = !poster">Posterise to 5 values</button>
          <p>The whole face crushed to the five-step scale. This is the drawing underneath the drawing — get these shapes right and blending is just tidying up.</p>
        </div>
      </div>
    </div>

    <!-- the filters the lab uses -->
    <svg class="defs" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <filter id="poster5" color-interpolation-filters="sRGB">
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncR type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncG type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncB type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
        </feComponentTransfer>
      </filter>
    </svg>

    <div class="drills">
      <article v-for="(d, i) in drills" :key="d.t" class="drill" v-reveal="i * 80">
        <span class="drill__n mono">Drill {{ String(i + 1).padStart(2, '0') }}</span>
        <h3>{{ d.t }}</h3>
        <p>{{ d.p }}</p>
      </article>
    </div>
  </SectionShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import SectionShell from '../ui/SectionShell.vue'
import HeadForm from '../svg/HeadForm.vue'

const grid = ref(true)
const cols = ref(5)
const rows = ref(7)
const isolate = ref(false)
const flipped = ref(false)
const squint = ref(false)
const poster = ref(false)

const focusC = 2
const focusR = 3

const cellX = computed(() => 420 / cols.value)
const cellY = computed(() => 560 / rows.value)

function setGrid(c, r) {
  cols.value = c
  rows.value = r
}

const stageFilter = computed(() => {
  const parts = []
  if (squint.value) parts.push('blur(5px)')
  if (poster.value) parts.push('url(#poster5)')
  return parts.length ? { filter: parts.join(' ') } : {}
})

const readout = computed(() => {
  const bits = []
  bits.push(grid.value ? `grid ${cols.value}×${rows.value}` : 'grid off')
  if (isolate.value) bits.push(`box ${focusR * cols.value + focusC + 1} isolated`)
  if (flipped.value) bits.push('inverted 180°')
  if (squint.value) bits.push('squint')
  if (poster.value) bits.push('5-value posterise')
  return bits.join('  ·  ')
})

const drills = [
  {
    t: 'The nonsense grid',
    p: 'Take a black-and-white photo, rule it into forty boxes, and number them. Copy the boxes in random order — 17, then 3, then 29. You never see the picture assemble, so you never start reciting.'
  },
  {
    t: 'The upside-down copy',
    p: 'Tape a reference to the wall upside down and draw it that way for a full sitting. Turn your paper the right way up only when you have finished. The result is almost always more accurate than anything you have drawn right way up.'
  },
  {
    t: 'The half-face match',
    p: 'Cut a magazine portrait vertically down the middle. Glue one half to your paper and draw the missing half so it matches. Nothing exposes a proportion error faster than having the correct answer glued beside it.'
  },
  {
    t: 'Negative space only',
    p: 'Draw only the shapes between things — the gap between the arm and the body, the wedge of background between two locks of hair. Those shapes have no names, so you copy them honestly.'
  }
]
</script>

<style scoped>
.lab {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}

.lab__stage {
  margin: 0;
  overflow: hidden;
  position: sticky;
  top: 92px;
  background: #080b10;
}
.stage__inner {
  position: relative;
  transition: transform 0.9s cubic-bezier(0.65, 0, 0.35, 1), filter 0.4s;
  transform-origin: 50% 50%;
}
.stage__inner.flip { transform: rotate(180deg); }
.stage__inner :deep(.headform) { display: block; width: 100%; }
.stage__grid {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.stage__cap {
  border-top: 1px solid var(--edge-soft);
  padding: 12px 16px;
  color: var(--signal);
  font-size: 9px;
  letter-spacing: 0.14em;
}

.lab__console { display: flex; flex-direction: column; gap: 22px; }
.console__hint { color: var(--text-dim); font-size: 14px; }
.ctl { transition: opacity 0.3s; }
.ctl.dim { opacity: 0.4; }
.ctl__row { display: flex; gap: 6px; flex-wrap: wrap; }
.ctl p {
  margin: 10px 0 0;
  color: var(--text-faint);
  font-size: 13px;
  line-height: 1.6;
  max-width: 46ch;
}

.defs { position: absolute; width: 0; height: 0; overflow: hidden; }

.drills {
  margin-top: clamp(50px, 7vh, 90px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1px;
  background: var(--edge-soft);
  border-top: 1px solid var(--edge-soft);
  border-bottom: 1px solid var(--edge-soft);
}
.drill { background: rgba(7, 10, 16, 0.7); padding: 26px 22px; }
.drill__n { color: var(--light); font-size: 9px; display: block; margin-bottom: 10px; }
.drill h3 { font-size: 17px; font-weight: 500; margin-bottom: 10px; }
.drill p { color: var(--text-dim); font-size: 13.5px; line-height: 1.62; margin: 0; }

@media (max-width: 980px) {
  .lab { grid-template-columns: 1fr; }
  .lab__stage { position: static; }
}
</style>
